/**
 * Public analytics API. This is the only module the rest of the app should
 * import from `analytics/`.
 *
 * Consent contract (see context/browser-storage-consent-context.tsx):
 *  - initializeAnalytics() must only be called after optional-storage consent
 *    is accepted. It lazy-loads posthog-js so the SDK is neither downloaded
 *    nor allowed to make network requests before that.
 *  - disableAnalytics() stops capture (including session replay) immediately.
 *  - resetAnalytics() clears PostHog's local persistence (distinct id,
 *    session) and must be called on logout and on consent withdrawal.
 */
import {
  capture,
  capturePageview,
  ensureInitialized,
  getAnalyticsConfig,
  identify,
  optIn,
  optOutAndStopRecording,
  resetIdentity,
} from "./client";
import {
  sanitizeEventProperties,
  sanitizeIdentifyProperties,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
  type AnalyticsIdentifyProperties,
} from "./events";
import type { AnalyticsClickId } from "./clicks";

export type { AnalyticsEventName, AnalyticsEventProperties } from "./events";
export type { AnalyticsClickId } from "./clicks";
export {
  AnalyticsPrivate,
  ANALYTICS_BLOCK_CLASS,
  ANALYTICS_MASK_CLASS,
  ANALYTICS_PRIVATE_ATTRIBUTE,
} from "./AnalyticsPrivate";

export function isAnalyticsConfigured(): boolean {
  return getAnalyticsConfig().enabled;
}

export async function initializeAnalytics(): Promise<void> {
  await ensureInitialized();
  optIn();
}

export function disableAnalytics(): void {
  optOutAndStopRecording();
}

export function resetAnalytics(): void {
  resetIdentity();
}

export function capturePageView(pathname: string): void {
  capturePageview(pathname);
}

export function captureEvent(
  event: AnalyticsEventName,
  properties?: AnalyticsEventProperties
): void {
  capture(event, sanitizeEventProperties(properties));
}

/**
 * Fires one of the hand-picked safe click identifiers (see analytics/clicks.ts).
 * Deliberately not gated on success — this answers "which controls do people
 * use", a separate question from the completion funnel events above.
 */
export function trackClick(id: AnalyticsClickId): void {
  capture(id, {});
}

export type IdentifyProperties = AnalyticsIdentifyProperties;

export function identifyUser(
  userId: string,
  properties: IdentifyProperties = {}
): void {
  if (!userId) return;
  identify(userId, sanitizeIdentifyProperties(properties));
}
